import { useState, useEffect } from 'react';
import { AlertTriangle, X, Clock } from 'lucide-react';
import styles from './ImpersonationBanner.module.css';

const STORAGE_KEY = 'enska_impersonation';

interface ImpersonationData {
  token: string;
  expiresAt: string;
  tenantName: string;
  supportEmail: string;
}

export function ImpersonationBanner() {
  const [data, setData] = useState<ImpersonationData | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as ImpersonationData;
      const exp = new Date(parsed.expiresAt).getTime();
      if (exp < Date.now()) { sessionStorage.removeItem(STORAGE_KEY); return; }
      setData(parsed);
      setSecondsLeft(Math.floor((exp - Date.now()) / 1000));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!data) return;
    const interval = setInterval(() => {
      const exp = new Date(data.expiresAt).getTime();
      const s = Math.floor((exp - Date.now()) / 1000);
      if (s <= 0) { endSession(); return; }
      setSecondsLeft(s);
    }, 1000);
    return () => clearInterval(interval);
  }, [data]);

  function endSession() {
    sessionStorage.removeItem(STORAGE_KEY);
    setData(null);
    window.location.href = '/admin/tenants';
  }

  if (!data) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div className={styles.banner}>
      <AlertTriangle size={16} className={styles.icon} />
      <span className={styles.text}>
        Support session active as <strong>{data.supportEmail}</strong>
        {data.tenantName && <> in <strong>{data.tenantName}</strong></>}
      </span>
      <span className={styles.timer}>
        <Clock size={13} />
        {mins}:{String(secs).padStart(2, '0')}
      </span>
      <button className={styles.endBtn} onClick={endSession}>
        <X size={14} /> End session
      </button>
    </div>
  );
}

/** Call this after receiving an impersonation token from the API */
export function startImpersonationSession(data: ImpersonationData) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}
